import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';
import { useIsMobile } from '@/hooks/use-mobile';
import AnimatedStat from '@/components/AnimatedStat';
import { getLatestInsights } from '@/data/insights';

const heroStats = [
  { value: 50, label: 'Projekte', suffix: '+' },
  { value: 100, label: 'Zufriedenheit', suffix: '%' },
  { value: 2, label: 'Köpfe – ein Team' },
];

const Hero = () => {
  const isMobile = useIsMobile();
  const latestInsight = getLatestInsights(1)[0];
  const words = ['Websites', 'KI-Strategien', 'Erlebnisse', 'Erfolge', 'Zukunft'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const heroBase = '/images/hero-bg';
  const heroSrc = buildSrc(heroBase, 1280);
  const heroSrcSet = buildSrcSet(heroBase, IMAGE_WIDTHS.hero);

  useEffect(() => {
    const currentWord = words[currentWordIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % words.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex, words]);


  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 section-dark">
      {/* Dark gradient background */}
      <div className="absolute inset-0">
        <img 
          src={heroSrc} 
          srcSet={heroSrcSet}
          sizes="100vw"
          alt="Abstraktes Hintergrundbild der Startseite"
          loading="eager"
          decoding="async"
          fetchPriority="high"
          width={1920}
          height={1080}
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--section-dark))]/90 via-[hsl(var(--section-dark))]/80 to-[hsl(var(--section-dark))]" />
      </div>

      {/* Animated Floating Elements - reduced on mobile for performance */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Only render heavy animations on desktop */}
        {!isMobile && (
          <>
            {/* Floating 3D Cubes with rotation */}
            <motion.div
              animate={{ 
                y: [0, -40, 0],
                x: [0, 15, 0],
                rotate: [0, 180, 360],
                rotateY: [0, 180, 360]
              }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-[15%] left-[8%] w-20 h-20"
              style={{ perspective: '1000px' }}
            >
              <div className="w-full h-full bg-gradient-to-br from-primary/50 to-primary/20 rounded-2xl backdrop-blur-sm border border-primary/40 shadow-2xl" 
                   style={{ transformStyle: 'preserve-3d' }} />
            </motion.div>
            
            <motion.div
              animate={{ 
                y: [0, 30, 0],
                x: [0, -20, 0],
                rotate: [0, -180, -360]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute top-[25%] right-[12%] w-16 h-16 bg-gradient-to-br from-glow-secondary/50 to-glow-secondary/20 rounded-xl backdrop-blur-sm border border-glow-secondary/40 shadow-xl"
            />
            
            <motion.div
              animate={{ 
                y: [0, -25, 0],
                x: [0, 10, 0],
                rotate: [0, 90, 0]
              }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
              className="absolute bottom-[35%] left-[15%] w-12 h-12 bg-gradient-to-br from-primary/40 to-glow-secondary/30 rounded-lg backdrop-blur-sm border border-primary/30 shadow-lg"
            />

            {/* Animated Hexagon */}
            <motion.div
              animate={{ 
                rotate: [0, 360],
                scale: [1, 1.1, 1]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-[40%] right-[25%] w-24 h-24"
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                <polygon 
                  points="50,5 90,25 90,75 50,95 10,75 10,25" 
                  fill="none" 
                  stroke="hsl(var(--primary) / 0.5)" 
                  strokeWidth="1.5"
                  className="drop-shadow-lg"
                />
              </svg>
            </motion.div>

            {/* Floating Circles */}
            <motion.div
              animate={{ 
                y: [0, 35, 0],
                x: [0, 20, 0],
                scale: [1, 1.2, 1]
              }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-[18%] right-[30%] w-24 h-24 bg-gradient-to-br from-glow-secondary/35 to-transparent rounded-full backdrop-blur-sm border border-glow-secondary/25"
            />
            <motion.div
              animate={{ 
                y: [0, -20, 0],
                x: [0, -15, 0]
              }}
              transition={{ duration: 11, repeat: Infinity, ease: "easeInOut", delay: 3 }}
              className="absolute bottom-[20%] right-[8%] w-18 h-18 bg-gradient-to-br from-primary/30 to-transparent rounded-full backdrop-blur-sm border border-primary/25"
            />

            {/* Animated Lines/Connectors */}
            <motion.div
              animate={{ 
                opacity: [0.3, 0.7, 0.3],
                scaleX: [0.8, 1, 0.8]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute top-[30%] left-[25%] w-32 h-[2px] bg-gradient-to-r from-transparent via-primary/60 to-transparent origin-left rotate-45"
            />
            <motion.div
              animate={{ 
                opacity: [0.3, 0.6, 0.3],
                scaleX: [0.9, 1.1, 0.9]
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
              className="absolute bottom-[40%] right-[20%] w-40 h-[2px] bg-gradient-to-r from-transparent via-glow-secondary/60 to-transparent origin-right -rotate-30"
            />

            {/* Floating Dots Pattern - reduced count */}
            {[...Array(4)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ 
                  y: [0, -15 - i * 5, 0],
                  opacity: [0.4, 0.9, 0.4]
                }}
                transition={{ 
                  duration: 3 + i, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: i * 0.5 
                }}
                className="absolute w-2 h-2 rounded-full bg-primary/70"
                style={{
                  top: `${20 + i * 15}%`,
                  left: `${5 + i * 10}%`
                }}
              />
            ))}

            {/* Animated Ring */}
            <motion.div
              animate={{ 
                rotate: [0, 360],
                scale: [0.95, 1.05, 0.95]
              }}
              transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
              className="absolute top-[10%] right-[5%] w-32 h-32 border-2 border-dashed border-primary/30 rounded-full"
            />
          </>
        )}

        {/* Glowing Orbs - simplified blur for better performance */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-primary/15 rounded-full blur-[60px] md:blur-[100px]"
        />
        <motion.div
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.15, 0.35, 0.15]
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute bottom-1/4 right-1/4 w-[250px] md:w-[400px] h-[250px] md:h-[400px] bg-glow-secondary/15 rounded-full blur-[50px] md:blur-[80px]"
        />
      </div>

      {/* Subtle Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.5) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--primary) / 0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Neuester Ratgeber-Artikel */}
          {latestInsight && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-4 flex justify-center"
            >
              <Link
                to={`/ratgeber/${latestInsight.slug}`}
                className="group inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-sm font-medium hover:bg-primary/25 transition-colors max-w-full"
              >
                <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                <span className="font-semibold uppercase tracking-wider text-xs">Neu im Ratgeber</span>
                <span className="hidden sm:inline text-primary/50">·</span>
                <span className="hidden sm:inline truncate max-w-[280px] md:max-w-[420px] text-primary/90 group-hover:text-primary">
                  {latestInsight.title}
                </span>
                <ArrowRight className="w-4 h-4 shrink-0 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </motion.div>
          )}

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
          >
            <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
            <span className="text-sm text-white/90">Webdesign, KI &amp; Digitalisierung aus Wernigerode im Harz</span>
          </motion.div>

          {/* Main Headline - White text on dark */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-tight mb-6 text-white"
          >
            Wir gestalten digitale
            <br />
            <span className="gradient-text text-glow">
              {displayText}
              <span className="animate-blink">|</span>
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-10"
          >
            Enno &amp; Jens entwickeln moderne Webseiten, SEO und KI-Lösungen – für den nachhaltigen Erfolg Ihres Unternehmens.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <motion.a
              href="#kontakt"
              className="group flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold text-lg hover-glow shadow-xl"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Projekt anfragen
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </motion.a>
            <motion.a
              href="#referenzen"
              className="flex items-center gap-2 px-8 py-4 rounded-xl border-2 border-white/30 text-white font-semibold text-lg hover:bg-white/10 transition-colors"
              whileHover={{ scale: 1.02 }}
            >
              Unsere Arbeiten
            </motion.a>
          </motion.div>

          {/* Stats - Glassmorphism on dark */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="grid grid-cols-3 gap-8 mt-20 mb-8 md:mb-16 max-w-2xl mx-auto bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20"
          >
            {heroStats.map((stat) => (
              <AnimatedStat
                key={stat.label}
                value={stat.value}
                label={stat.label}
                suffix={stat.suffix}
                className="text-center"
                valueClassName="text-3xl md:text-4xl font-display font-bold gradient-text"
                labelClassName="text-sm text-white/80 mt-1"
              />
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        aria-hidden="true"
      >
        <div className="w-6 h-10 rounded-full border-2 border-white/40 flex items-start justify-center p-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 rounded-full bg-primary"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
